import {
  meetsMinimumSessionCohortSize,
  type SessionComparisonForSession,
  type SessionComparisonMetricKey,
  type SessionMetricRank,
} from "./lib/sessionProductivityComparison.js";

export const SESSION_COMPARISON_INTRO = "Let's analyze your session...";

const METRIC_CONFIG: Record<
  SessionComparisonMetricKey,
  { label: string; worstAdvice: string }
> = {
  points: {
    label: "session score",
    worstAdvice: "aim for one more focused block tomorrow.",
  },
  durationMinutes: {
    label: "focused minutes",
    worstAdvice: "schedule a longer block next time.",
  },
  tasksCompleted: {
    label: "tasks completed",
    worstAdvice: "review your time estimates before you dive in.",
  },
  distractionMinutes: {
    label: "distractions",
    worstAdvice: "try locking your phone when you start your session.",
  },
};

const METRIC_KEYS: SessionComparisonMetricKey[] = [
  "points",
  "durationMinutes",
  "tasksCompleted",
  "distractionMinutes",
];

export interface SessionComparisonCopy {
  intro?: string;
  lines: string[];
}

function formatTopLine(metricKey: SessionComparisonMetricKey, rank: SessionMetricRank): string {
  const { label } = METRIC_CONFIG[metricKey];
  return `You're in the top ${rank.topPercentCeiling}%ile of all members in terms of ${label} today - nice.`;
}

function formatBottomLine(metricKey: SessionComparisonMetricKey, rank: SessionMetricRank): string {
  const { label, worstAdvice } = METRIC_CONFIG[metricKey];
  return `You're in the bottom ${rank.bottomPercentCeiling}%ile of all members in terms of ${label} today - ${worstAdvice}`;
}

/**
 * Picks the strongest and weakest metric by cohort rank (distinct keys when possible).
 */
export function pickBestAndWorstMetrics(
  ranks: SessionComparisonForSession["ranks"]
): { best: SessionComparisonMetricKey; worst: SessionComparisonMetricKey } {
  const sortedByBest = [...METRIC_KEYS].sort(
    (a, b) => ranks[a].topPercentCeiling - ranks[b].topPercentCeiling
  );
  const best = sortedByBest[0]!;
  const worstCandidates = sortedByBest.filter((key) => key !== best);
  const worst =
    worstCandidates.length > 0
      ? worstCandidates.reduce((acc, key) =>
          ranks[key].bottomPercentCeiling > ranks[acc].bottomPercentCeiling ? key : acc
        )
      : best;
  return { best, worst };
}

/**
 * Formats per-session comparison intro + lines for Morning After session cards.
 */
export function formatSessionComparisonCopy(
  comparison: SessionComparisonForSession | undefined
): SessionComparisonCopy {
  if (!comparison || !meetsMinimumSessionCohortSize(comparison.cohort.sessionCount)) {
    return { lines: [] };
  }

  const { best, worst } = pickBestAndWorstMetrics(comparison.ranks);
  const lines: string[] = [formatTopLine(best, comparison.ranks[best])];

  if (worst !== best) {
    lines.push(formatBottomLine(worst, comparison.ranks[worst]));
  }

  return {
    intro: SESSION_COMPARISON_INTRO,
    lines,
  };
}

/**
 * @deprecated Prefer {@link formatSessionComparisonCopy} for intro + lines.
 */
export function formatSessionComparisonLines(
  comparison: SessionComparisonForSession | undefined
): string[] {
  return formatSessionComparisonCopy(comparison).lines;
}

/**
 * Visual tone for session comparison lines on email session cards.
 */
export type SessionComparisonLineTone = "positive" | "challenge" | "distraction";

export function getSessionComparisonLineTone(line: string): SessionComparisonLineTone {
  if (/bottom \d+%ile/i.test(line) && /distractions/i.test(line)) {
    return "distraction";
  }
  if (/bottom \d+%ile/i.test(line)) {
    return "challenge";
  }
  return "positive";
}

export type SessionComparisonLineSegment = {
  emphasis: boolean;
  text: string;
};

/**
 * Splits a comparison line for emphasis styling (single-line copy uses full emphasis).
 */
export function splitSessionComparisonLine(line: string): SessionComparisonLineSegment[] {
  return [{ emphasis: true, text: line }];
}
